<template>
  <v-sheet class="pa-4">
    <div>
      <h3>Пересчитать баллы по пользователю</h3>
      <slot></slot>
    </div>
    <validation-observer
      ref="form"
      tag="form"
      class="mt-4"
      mode="lazy"
      @submit.prevent="handleSubmit"
    >
      <div>
        <v-radio-group v-model="form.type" row>
          <v-radio label="Ввести ID вручную" value="string"></v-radio>
          <v-radio label="Загрузить CSV" value="csv"></v-radio>
        </v-radio-group>

        <validation-provider
          v-if="form.type === 'string'"
          v-slot="{ errors }"
          :rules="{ ids: true, required: true }"
          name="user_id"
        >
          <v-text-field
            v-model="form.user_ids"
            label="ID Пользователей (через запятую)"
            type="string"
            :error-messages="errors"
            :persistent-hint="true"
            hint="Можно указать несколько id через запятую"
            name="user_ids"
          ></v-text-field>
        </validation-provider>

        <validation-provider
          v-else
          v-slot="{ errors }"
          :rules="{ required: true }"
          name="csv_file"
        >
          <v-file-input
            v-model="form.csv_file"
            accept=".csv,text/csv"
            label="CSV файл с ID пользователей"
            placeholder="Выберите CSV файл"
            :error-messages="errors"
            show-size
            truncate-length="35"
            name="csv_file"
            hint="Только список id пользователей, разделенных запятыми или переносами строк"
          ></v-file-input>
        </validation-provider>
      </div>
      <validation-provider
        v-slot="{ errors }"
        :rules="{ required: true }"
        slim
        name="comp_id"
      >
        <v-select
          v-model="form.competence_id"
          :items="compList"
          item-text="title"
          item-value="id"
          label="Компетенция"
          name="comp_id"
          :error-messages="errors"
          required
        ></v-select>
      </validation-provider>

      <v-btn type="submit" :loading="pending" color="primary"
        >Пересчитать баллы</v-btn
      >
    </validation-observer>

    <div v-if="taskResults.progress" class="my-4">
      <v-progress-linear
        :value="(taskResults.progress / taskResults.total) * 100"
        color="primary"
        height="20"
      >
        <strong class="text-white"
          >{{
            Math.round((taskResults.progress / taskResults.total) * 100)
          }}%</strong
        >
      </v-progress-linear>
      <div class="mt-4">
        Обработано {{ taskResults.progress }} из {{ taskResults.total }}.
      </div>
    </div>

    <div v-if="taskResults.errors.length" class="my-4">
      <div class="mb-4">
        Не удалось создать задачу по синхронизации баллов для следующих
        talent_id:
      </div>
      <ul>
        <li v-for="error in taskResults.errors" :key="error.id" class="mb-2">
          ID Пользователя:
          <a
            rel="noopener noreferrer"
            :href="`${CRM_URL}/contacts/${error.id}`"
            target="_blank"
            >{{ error.id }}</a
          >, Ошибка: {{ error.error }}
        </li>
      </ul>
    </div>
  </v-sheet>
</template>

<script>
import { apiClient } from "@/api";

const isTalent = (title) => title.startsWith("Конкурс Талант -");

export default {
  name: "CalcUserScore",
  props: {
    userId: {
      type: Number,
      required: false,
    },

    compId: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      form: {
        type: "string", // string | csv
        competence_id: this.compId ?? null,
        user_ids: this.userId ? String(this.userId) : "",
        csv_file: null,
      },
      pending: false,
      taskResults: {
        total: 0, // Общее количество задач для создания
        progress: 0, // Количество задач, которые уже обработаны (успешно или с ошибкой)
        doneCount: 0, // Количество успешно созданных задач
        /**
         * {id: number, error: string}[]
         */
        errors: [],
      },
    };
  },
  computed: {
    compList() {
      const all = this.$store.state.competences;
      const groups = this.$store.state.groups;
      return all
        .map((c) => {
          const groupName =
            groups[c.group_id]?.name || c.group_id || "Без группы";
          return {
            id: c.id,
            title: `${groupName} - ${c.name} `,
          };
        })
        .sort((a, b) => {
          const bisTalent = isTalent(b.title);
          const aisTalent = isTalent(a.title);
          if (aisTalent && !bisTalent) {
            return -1;
          }
          if (!aisTalent && bisTalent) {
            return 1;
          }

          return b.title.localeCompare(a.title);
        });
    },
  },
  watch: {
    userId(newVal) {
      this.form.user_ids = newVal;
    },
    compId(newVal) {
      this.form.competence_id = newVal;
    },
  },
  async mounted() {
    if (!this.$store.state.competences?.length) {
      await this.$store.dispatch("getAllCompetence");
      await this.$store.dispatch("getAllGroups");
    }
  },
  created() {
    this.CRM_URL = process.env.VUE_APP_CRM_ORIGIN;
  },
  methods: {
    readCsv(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve(String(event.target?.result || ""));
        };
        reader.onerror = () => {
          reject(new Error("Не удалось прочитать CSV файл"));
        };
        reader.readAsText(file);
      });
    },
    async getIdsFromCsv(file) {
      const content = await this.readCsv(file);
      return content
        .split(/[\n,]+/)
        .map((value) => Number(String(value).trim()))
        .filter((value) => Number.isFinite(value) && value > 0);
    },
    /**
     *
     * @param userIds {number[]}
     * @param competenceId {number}
     * @returns {Promise<void>}
     */
    async runUsers(userIds, competenceId) {
      if (!Array.isArray(userIds) || !competenceId) {
        return;
      }
      this.taskResults.doneCount = 0;
      this.taskResults.errors = [];
      this.taskResults.total = userIds.length;
      this.taskResults.progress = 0;

      const requests = userIds.map((userId) =>
        apiClient({
          method: "POST",
          url: `/competences/${competenceId}/users_tasks`,
          data: {
            user_id: userId,
          },
        })
      );

      let doneCount = 0;
      const errors = [];

      try {
        const results = await Promise.allSettled(requests);

        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            doneCount++;
          } else {
            const error = result.reason;
            errors.push({
              id: userIds[index],
              error: error.message,
            });
          }
          this.taskResults.progress++;
        });
        this.taskResults.doneCount = doneCount;
        this.taskResults.errors = errors;
      } catch (error) {
        this.$toast(
          `Произошла ошибка при отправке запросов. ${error.message}`,
          {
            type: "error",
          }
        );
        return;
      }
    },
    async handleSubmit() {
      const { form } = this;
      const isValid = await this.$refs.form.validate();
      if (!isValid) {
        return;
      }

      this.pending = true;

      try {
        let allIds = [];
        if (form.type === "csv") {
          allIds = await this.getIdsFromCsv(form.csv_file);
        } else {
          allIds = form.user_ids
            .split(",")
            .map((id) => Number(id.trim()))
            .filter((id) => !!id);
        }

        const ids = [...new Set(allIds)];
        await this.runUsers(ids, form.competence_id);
      } catch (error) {
        this.$toast(`Ошибка обработки файла: ${error.message}`, {
          type: "error",
        });
      } finally {
        this.pending = false;
      }
    },
  },
};
</script>

<style></style>
